import React, { CSSProperties, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { colors, css, fonts, frames, getGraphColor, glyphs } from '../../../../Styles';
import { Overlay, PageHeader, TableLayout, TableTypes } from '../../../../Components';
import { ButtonTypes, ListData_ValueTypes, TableStyles_Types, ValueSteps } from '../../../../Definitions'
import { Graph_QL, useFeedComponents, FeedComponentInfo } from '../../../../Services';
import { RootState, useAppSelector } from '../../../../Store';
import { keyBy, merge } from 'lodash';
import { Pages } from '../../../../Navigation/root';
import { useReactTable, getCoreRowModel, getFilteredRowModel, getSortedRowModel } from '@tanstack/react-table';

// table
import { ReactiveTable } from '../../../../Components/Tables/reactiveTableRenderer';
import { GenericDataParameters, Units, useColumnConstructor } from '../../../../Components/Tables/_Tables/useColumnConstructor';
import { NewButtonList } from '../../../../Components/BaseComponents/newButton';
import { useDispatch, useSelector } from 'react-redux';
import { SET_EDIT_MODE } from '../../../../Store/UI/editModeSlice';

const getComponentTableDivStyle = (id?: string | number):CSSProperties|undefined => { if (!id) {return undefined} return {backgroundColor: getGraphColor(+id), ...css.colorTag, ...fonts.header, ...fonts.fontSize4, color: colors.whitePrimary}}
interface TableData {
  id: number,
  [key: string]: string|number|boolean|undefined
}

const getUnitFromAccessor = (accessor: keyof FeedComponentInfo) => {
  if (accessor === "ds" || accessor === "protein") {
    return Units.kg
  }
  if (accessor === "energy") {
    return Units.energy
  }
  return 
}

export default function Components() {
  const { t } = useTranslation();
  const dispatchEditMode = useDispatch();
  const editMode = useSelector((state: RootState) => state.uiState.editMode.editMode);
  // const [editTable, setEditTable] = useState(false);
  const [addedRowCounter, setAddedRowCount] = useState(1);
  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({domain: farm})),[farm]);

  const feedComponentAccessorKeys = useMemo(() => ({
    ...(editMode ? { delete: ListData_ValueTypes.DeleteRowAction } : {}),
    name: ListData_ValueTypes.Div,
    readyMixture: ListData_ValueTypes.Check,
    priority: ListData_ValueTypes.Number,
    ds: ListData_ValueTypes.Number,
    energy: ListData_ValueTypes.Number,
    protein: ListData_ValueTypes.Number,
  }), [editMode]);
  
  const {call: getFeedComponentsInfo, data: feedComponentsState, maintainEntries} = useFeedComponents(connection);
 
  // Switch between edit mode and non-edit mode
  // const switchTable = () => {
  //   setEditTable((editTable) => !editTable);
  // };

  const feedComponents = useMemo(() => feedComponentsState && feedComponentsState[domain] ? feedComponentsState[domain].data: undefined,[domain, feedComponentsState])
  const [data, setData] = useState(feedComponents?.filter((component) => !component.deleted) ?? []); // TODO CTO check if we aren't already filtering deleted components (we should be)

  useEffect(() => {
    if (feedComponents) {
      setData(feedComponents.filter((component) => !component.deleted));
    }
  },[feedComponents])
  const dataRef = useRef<TableData[]>([]);

  const saveChangedData = () => {
    if (maintainEntries) {
      const data = dataRef.current.map((tableData) => {
        const foundFeedComponents = feedComponents?.find((component) => component.id === tableData.id);
        if (foundFeedComponents === undefined) {
          const defaultValues: FeedComponentInfo = {id: -1, name: "Feed Component", readyMixture: false, priority: 1, ds: 0, energy: 0, protein: 0, deleted: false}
          const suggestedDataEntry = { ...defaultValues, ...tableData };
          return suggestedDataEntry;
        }
        const suggestedDataEntry = {...foundFeedComponents, ...tableData };
        delete suggestedDataEntry["__typename"];
        return suggestedDataEntry;
      });
      maintainEntries({con: connection.connection, data: data});
      dataRef.current = [];
    }
  }

  const clearDirt = useCallback(() => {
    if (feedComponents) {
      setData(feedComponents.filter((component) => !component.deleted));
      dataRef.current = [];
    }
  },[feedComponents])

  const addRow = useCallback(() => {
    // setEditTable(true);
    dispatchEditMode(SET_EDIT_MODE(true));
    setAddedRowCount(addedRowCounter + 1);
    setData([{id: -addedRowCounter, name: "", readyMixture: false, priority: 0, ds: 0, energy: 0, protein: 0, deleted: false}, ...Object.values(merge([], keyBy(data, "id"), keyBy(dataRef.current, "id"))).filter((row) => !row.deleted)]);
  },[addedRowCounter, data])

  useEffect(() => {
    getFeedComponentsInfo({fetchAmount: 50}, connection);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  const accessorData = useMemo<GenericDataParameters<FeedComponentInfo>[]>(() => {
    return Object.entries(feedComponentAccessorKeys).map(([accessor, type]) => {
      return {
        headerValue: t(`feedCurvePages.components.${accessor}`, {defaultValue: type}),
        accessor,
        type,
        unit: getUnitFromAccessor(accessor as keyof FeedComponentInfo),
        width: accessor ==="delete" ? "40px" : accessor ==="name" ? "200px" : undefined,
        sort: accessor ==="delete" ? false : true,
        divStyleOverride: type === ListData_ValueTypes.Div ? getComponentTableDivStyle : undefined,
        styleOverrideOnValue: false,
        valueStep: type === ListData_ValueTypes.Number && accessor === "priority" ? ValueSteps.integers : ValueSteps.floats,
      }
    })
  },[feedComponentAccessorKeys, t]);
  const columns = useColumnConstructor<FeedComponentInfo>({accessorData, tableStyle: TableStyles_Types.primaryTable, canEdit: editMode})
  
  const table = useReactTable({
    columns: columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    meta: {
      updateData: (id, columnId, value) => {
        dataRef.current = (Object.values(merge(keyBy(dataRef.current, "id"), keyBy([{id, [columnId]: value}], "id"))))
      },
      deleteRow: (deletedRowId) => {
        dataRef.current = Object.values(merge(keyBy(dataRef.current, "id"),keyBy([{id: deletedRowId, deleted: true}], "id")));
        setData(Object.values(merge([], keyBy(data, "id"), keyBy(dataRef.current, "id"))).filter((row) => !row.deleted));
      }
    },
    state: {
      columnPinning: {
        left: !editMode ? ["name"] : []
      }
    }
  })

// #region | Button List
  const useContents = (): [string[], JSX.Element[], (() => void)[]] => {
    const buttonEntries: { label: string; glyphs: JSX.Element; onClick: () => void; }[] = [];

    if (editMode) {
      buttonEntries.push(
        { label: t("buttons.save", { defaultValue: "save" }), glyphs: glyphs.Check({}), onClick: () => { saveChangedData(); dispatchEditMode(SET_EDIT_MODE(false)) }},
        { label: t("buttons.cancel", { defaultValue: "cancel" }), glyphs: glyphs.Close({}), onClick: () => { clearDirt(); dispatchEditMode(SET_EDIT_MODE(false)) }}
      )
    }

    buttonEntries.push(
      { label: t("buttons.add", { defaultValue: "add" }), glyphs: glyphs.Add({}), onClick: () => { addRow(); dispatchEditMode(SET_EDIT_MODE(true))} },
      { label: t("buttons.edit", { defaultValue: "edit" }), glyphs: glyphs.Edit({}), onClick: () => { dispatchEditMode(SET_EDIT_MODE(true)) } }
    )

    const labels = buttonEntries.map((b) => b.label);
    const icons = buttonEntries.map((b) => b.glyphs)
    const onclicks = buttonEntries.map((b) => b.onClick);
    return [labels, icons, onclicks];
  };

  const ButtonRow = memo(({ editMode }: { editMode: boolean }) => {
    const [contentsRes, icons, onclicks] = useContents();

    return (
      <NewButtonList
        contents={contentsRes}
        onClick={(i) => { onclicks[i]?.() }}
        buttonType={ButtonTypes.tableButton}
        startEnhancers={icons}
        disabled={editMode ? [false, false, true, true] : [false, false]}
        overrides={(index) => ({
          baseStyles: { backgroundColor: editMode && index === 3 ? colors.greyPrimary : colors.greySecondary },
          disabledStyles: { opacity: editMode && (index === 2 || index > 3) ? 0.5 : 1 },
        })}
      />
    );
  });
  // #endregion

  if (!feedComponents) { return (<div></div>) }
  
  return (
    <frames.Page_inner>
      <PageHeader topHeader={t(`menuHeaders.${Pages.feedCurves}`, {defaultValue: "feed curves"})} subHeader={t(`menuHeaders.${Pages.components}`, {defaultValue: "components"})}/>
      <frames._page_inner_content>
        <TableLayout
          tableType={TableTypes.fullPage}
          zIndex={editMode ? 999 : 0}
          buttonArea={<ButtonRow editMode={editMode}/>}
          tableArea={<ReactiveTable minRows={30} table={table} tableType={TableStyles_Types.primaryTable} emptyText={t("feedCurvePages.components.emptyText", {defaultValue: "No components found"})} tableToggled={editMode}/>}
        />
      </frames._page_inner_content>
      {editMode ? <Overlay overrides={{ zIndex: editMode ? 9 : 0 }} beforeUnload onClickAlert/> : <></>}
    </frames.Page_inner>
  );
}